import { FileUpload } from "@ark-ui/react";
import {
  MacAddressSchema,
  ResidentBatchRegistrationHeaderRows,
  SENSOR_TYPE,
  SEX,
  SIMPLE_INDEPENDENCE_LEVEL,
  SensorBatchRegistrationHeaderRows,
  SimpleIndependenceLevelBatchRegistrationHeaderRows,
  toPathSafeString,
  toResidentBatchRegistrationRow,
  toSensorBatchRegistrationRow,
  toSimpleIndependenceLevelBatchRegistrationRow,
} from "@packages/core";
import { Link, createFileRoute } from "@tanstack/react-router";
import Encoding from "encoding-japanese";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAsync } from "react-use";
import { Temporal } from "temporal-polyfill";
import * as v from "valibot";

import {
  ButtonStyle2,
  FileUploadStyle,
  FormField,
  FormFieldInput,
  PageName,
  PaperStyle,
  SwitchStyle,
  TypographyStyle,
  showMessage,
} from "#components";
import {
  BedBatchAllocationHeaderRows,
  BedBatchRegistrationHeaderRows,
  toBedBatchAllocationRow,
  toBedBatchRegistrationRow,
} from "#models";
import { isNativeApplication, saveAsFile, useEasy, useVolume } from "#services";
import { css, cx } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/management/utilities/")({
  component: Component,
});

function Component() {
  const { t } = useTranslation();

  return (
    <main>
      <PageName>{t("%Utility.0%")}</PageName>
      <h1 className={TypographyStyle({ fontSize: "24px", fontWeight: 700, lineHeight: 1 })}>
        {t("%Utility.0%")}
      </h1>

      <PreferencesPane />

      <div
        className={css({
          backgroundColor: "#FAFAFA",
          padding: "16px 24px",
          borderRadius: "12px",
          marginTop: "16px",
        })}
      >
        <h2 className={TypographyStyle({ fontSize: "16px", fontWeight: 600, lineHeight: 1 })}>
          {t("%AamsResidentListFileSplitter.0%")}
        </h2>

        <div
          className={css(
            PaperStyle.raw({ borderRadius: "12px", marginTop: "12px", padding: "16px 12px" }),
            {
              borderColor: "border",
              borderStyle: "solid",
              borderWidth: "1px",
              boxShadow: "0px 1px 2px 0px #0000000F,0px 1px 3px 0px #0000001A",
            },
          )}
        >
          <p className={TypographyStyle({ fontSize: "14px" })}>
            {t("%AamsResidentListFileSplitter.1%")}
          </p>
          <p className={TypographyStyle({ color: "error", fontSize: "14px", marginTop: "12px" })}>
            {t("%AamsResidentListFileSplitter.2%")}
          </p>
          <p
            className={TypographyStyle({
              color: "error",
              fontSize: "14px",
              fontWeight: 500,
              marginTop: "12px",
            })}
          >
            {t("%AamsResidentListFileSplitter.3%")}
          </p>

          <AamsResidentListFileSplitter className={css({ marginTop: "16px" })} />
        </div>
      </div>

      <div
        className={css({
          backgroundColor: "#FAFAFA",
          padding: "16px 24px",
          borderRadius: "12px",
          marginTop: "16px",
        })}
      >
        {isNativeApplication() && (
          <Link className={ButtonStyle2({ color: "black" })} to="/debug/native-application">
            {t("%Debug.0%")}
          </Link>
        )}
      </div>
    </main>
  );
}

function PreferencesPane() {
  const { t } = useTranslation();

  const [easy, setEasy] = useEasy();
  const [volume, setVolume] = useVolume();

  useEffect(
    function () {
      const timer = setTimeout(function () {
        setEasy(easy);
      }, 250);
      return function () {
        clearTimeout(timer);
      };
    },
    [easy, setEasy],
  );

  useEffect(
    function () {
      const timer = setTimeout(function () {
        setVolume(volume);
      }, 250);
      return function () {
        clearTimeout(timer);
      };
    },
    [setVolume, volume],
  );

  return (
    <div
      className={css({
        backgroundColor: "#FAFAFA",
        padding: "16px 24px",
        borderRadius: "12px",
        marginTop: "16px",
      })}
    >
      <h2 className={TypographyStyle({ fontSize: "16px", fontWeight: 600, lineHeight: 1 })}>
        {t("%BrowserSettings.0%")}
      </h2>

      <div
        className={css(
          PaperStyle.raw({ borderRadius: "12px", marginTop: "12px", padding: "16px 12px" }),
          {
            borderColor: "border",
            borderStyle: "solid",
            borderWidth: "1px",
            boxShadow: "0px 1px 2px 0px #0000000F,0px 1px 3px 0px #0000001A",
          },
        )}
      >
        <FormField description={t("%EasyMode.1%")} label={t("%EasyMode.0%")}>
          <FormFieldInput
            checked={easy}
            className={SwitchStyle({ display: "block" })}
            onChange={function (event) {
              setEasy(event.target.checked);
            }}
            type="checkbox"
          />
        </FormField>

        <FormField
          className={css({ marginTop: "16px" })}
          description={t("%Volume.1%")}
          label={t("%Volume.0%")}
        >
          <div className={css({ display: "flex", alignItems: "center", gap: "12px" })}>
            <FormFieldInput
              max="100"
              min="0"
              onChange={function (event) {
                let volume;
                try {
                  volume = Number.parseInt(event.target.value) / 100;
                } catch {
                  volume = 1.0;
                }
                setVolume(volume);
              }}
              type="range"
              value={volume * 100}
            />
            <span className={TypographyStyle({ fontSize: "12px", fontWeight: 500, lineHeight: 1 })}>
              {Math.round(volume * 100)}%
            </span>
          </div>
        </FormField>
      </div>
    </div>
  );
}

type AamsResidentListFileSplitterProperties = FileUpload.RootProps;
function AamsResidentListFileSplitter({
  className,
  ...properties
}: AamsResidentListFileSplitterProperties) {
  const NUMBER_OF_HEADER_ROWS = 3;

  const { t } = useTranslation();
  const [files, setFiles] = useState<Array<File>>([]);

  const schema = useMemo(function () {
    const dictionary: Partial<Record<string, string>> = {
      "０": "0",
      "１": "1",
      "２": "2",
      "３": "3",
      "４": "4",
      "５": "5",
      "６": "6",
      "７": "7",
      "８": "8",
      "９": "9",
      "．": ".",
    };
    function sanitizeNumberString(input: string) {
      return input.replaceAll(/./g, function (match) {
        return dictionary[match] ?? match;
      });
    }

    const simpleIndependenceLevelSchema = v.union([
      v.pipe(
        v.picklist(["1", "１"]),
        v.transform<"1" | "１", typeof SIMPLE_INDEPENDENCE_LEVEL.COMPLETE_ASSISTANCE>(function () {
          return SIMPLE_INDEPENDENCE_LEVEL.COMPLETE_ASSISTANCE;
        }),
      ),
      v.pipe(
        v.picklist(["2", "２"]),
        v.transform<"2" | "２", typeof SIMPLE_INDEPENDENCE_LEVEL.PARTIAL_ASSISTANCE>(function () {
          return SIMPLE_INDEPENDENCE_LEVEL.PARTIAL_ASSISTANCE;
        }),
      ),
      v.pipe(
        v.picklist(["3", "３"]),
        v.transform<"3" | "３", typeof SIMPLE_INDEPENDENCE_LEVEL.INDEPENDENCE>(function () {
          return SIMPLE_INDEPENDENCE_LEVEL.INDEPENDENCE;
        }),
      ),
    ]);

    return v.pipe(
      v.tuple([
        /** 番号 */
        v.unknown(),
        /** 居室の名前 */
        v.pipe(v.string(), v.trim(), v.maxGraphemes(64)),
        /** aamsのMACアドレス */
        MacAddressSchema(),
        /** 入居者の名前 */
        v.pipe(v.string(), v.trim(), v.maxGraphemes(32)),
        /** 入居者の年齢 */
        v.pipe(
          v.string(),
          v.trim(),
          v.nonEmpty(),
          v.transform(function (input) {
            return Number.parseInt(sanitizeNumberString(input));
          }),
          v.gtValue(0),
          v.maxValue(255),
        ),
        /** 入居者の性別 */
        v.union([
          v.pipe(
            v.union([v.literal("0"), v.literal("０")]),
            v.transform<"0" | "０", typeof SEX.FEMALE>(function () {
              return SEX.FEMALE;
            }),
          ),
          v.pipe(
            v.union([v.literal("1"), v.literal("１")]),
            v.transform<"1" | "１", typeof SEX.MALE>(function () {
              return SEX.MALE;
            }),
          ),
        ]),
        /** 入居者の身長 */
        v.pipe(
          v.string(),
          v.trim(),
          v.nonEmpty(),
          v.transform(function (input) {
            return Number.parseFloat(sanitizeNumberString(input));
          }),
          v.gtValue(0),
          v.maxValue(999),
        ),
        /** 入居者の体重 */
        v.pipe(
          v.string(),
          v.trim(),
          v.nonEmpty(),
          v.transform(function (input) {
            return Number.parseFloat(sanitizeNumberString(input));
          }),
          v.gtValue(0),
          v.maxValue(999),
        ),
        simpleIndependenceLevelSchema,
        simpleIndependenceLevelSchema,
        simpleIndependenceLevelSchema,
        simpleIndependenceLevelSchema,
      ]),
      v.transform(function ([
        ,
        roomName,
        macAddress,
        residentName,
        age,
        sex,
        height,
        weight,
        simpleIndependenceLevelL,
        simpleIndependenceLevelI,
        simpleIndependenceLevelQ,
        simpleIndependenceLevelR,
      ]) {
        return {
          roomName,
          macAddress,
          residentName,
          age,
          sex,
          height,
          weight,
          simpleIndependenceLevelL,
          simpleIndependenceLevelI,
          simpleIndependenceLevelQ,
          simpleIndependenceLevelR,
        };
      }),
    );
  }, []);

  const state = useAsync(
    async function () {
      for (let f = 0; f < files.length; f++) {
        const file = files[f];

        const facilityName = file.name.split("_").at(0)?.replace(/様$/, "") ?? t("%Unknown.0%");

        let rows;
        try {
          const bytes = new Uint8Array(await file.arrayBuffer());
          const t = Encoding.convert(bytes, { from: "SJIS", to: "UNICODE" });
          const text = Encoding.codeToString(t) as string;
          rows = text.split(/\r\n|\n|\r/);
        } catch (error) {
          console.error(error);
          showMessage({
            body: t("%Import.1%", { $: { file } }),
            color: "error",
            title: t("%Error.0%"),
            icon: undefined,
            type: "toast",
          });
          continue;
        }

        console.info("ファイルを読み込みました。", rows);

        const numberOfBeds = new Map<string, number>();
        const allocations = [];
        const residents = [];
        const macAddresses = new Set<string>();

        for (let r = NUMBER_OF_HEADER_ROWS; r < rows.length; r++) {
          const row = rows[r];

          const cells = row.split(",");
          if (
            cells.slice(1).every(function (cell) {
              return cell.trim() === "";
            })
          ) {
            // 2列目以降が全て空欄の場合は処理をスキップする。
            continue;
          }

          let data;
          try {
            data = v.parse(schema, cells);
          } catch (error) {
            console.error(error);
            showMessage({
              body: t("%Import.2%", { $: { file, row: r + 1 } }),
              color: "error",
              title: t("%Error.0%"),
              icon: undefined,
              type: "toast",
            });
            return;
          }

          const {
            age,
            height,
            macAddress,
            residentName,
            roomName,
            sex,
            simpleIndependenceLevelI,
            simpleIndependenceLevelL,
            simpleIndependenceLevelQ,
            simpleIndependenceLevelR,
            weight,
          } = data;

          allocations.push({
            bed: { roomName },
            resident: { name: residentName },
            sensor: { macAddress, type: SENSOR_TYPE.AAMS },
          } as const);

          if (macAddresses.has(macAddress)) {
            // MACアドレスが重複している場合は中断する。
            showMessage({
              body: t("%Import.2%", { $: { file, row: r + 1 } }),
              color: "error",
              title: t("%Error.0%"),
              icon: undefined,
              type: "toast",
            });
            return;
          } else {
            macAddresses.add(macAddress);
          }

          numberOfBeds.set(roomName, (numberOfBeds.get(roomName) ?? 0) + 1);

          residents.push({
            birthday: Temporal.Now.plainDateISO()
              .subtract({ years: age })
              .with({ month: 1, day: 1 }),
            height,
            name: residentName,
            sex,
            simpleIndependenceLevelI,
            simpleIndependenceLevelL,
            simpleIndependenceLevelQ,
            simpleIndependenceLevelR,
            weight,
          });

          showMessage({
            body: t("%Import.4%", { $: { file, row: r + 1 } }),
            color: "success",
            title: t("%Success.0%"),
            icon: undefined,
            type: "toast",
          });
        }

        saveAsFile(
          new Blob(
            [
              new Uint8Array(
                Encoding.convert(
                  Encoding.stringToCode(
                    SensorBatchRegistrationHeaderRows({ translate: t })
                      .concat(
                        Array.from(macAddresses.values()).map(function (macAddress, index) {
                          return toSensorBatchRegistrationRow(
                            {
                              macAddress,
                              name: "aams" + String(index + 1).padStart(3, "0"),
                              type: SENSOR_TYPE.AAMS,
                            },
                            { translate: t },
                          );
                        }),
                      )
                      .join("\r\n"),
                  ),
                  { from: "UNICODE", to: "SJIS" },
                ),
              ),
            ],
            { type: "text/csv" },
          ),
          t("%SensorBatchRegistration.10%", {
            $: { facilityName, now: Temporal.Now.plainDateTimeISO("Asia/Tokyo") },
          }),
        );

        saveAsFile(
          new Blob(
            [
              new Uint8Array(
                Encoding.convert(
                  Encoding.stringToCode(
                    ResidentBatchRegistrationHeaderRows({ translate: t })
                      .concat(
                        residents.map(function (resident) {
                          return toResidentBatchRegistrationRow(
                            { ...resident, agreed: true, displayName: "", pronunciationName: "" },
                            { translate: t },
                          );
                        }),
                      )
                      .join("\r\n"),
                  ),
                  { from: "UNICODE", to: "SJIS" },
                ),
              ),
            ],
            { type: "text/csv" },
          ),
          t("%ResidentBatchRegistration.8%", {
            $: { facilityName, now: Temporal.Now.plainDateTimeISO("Asia/Tokyo") },
          }),
        );

        saveAsFile(
          new Blob(
            [
              new Uint8Array(
                Encoding.convert(
                  Encoding.stringToCode(
                    SimpleIndependenceLevelBatchRegistrationHeaderRows({ translate: t })
                      .concat(
                        residents.map(function (resident) {
                          return toSimpleIndependenceLevelBatchRegistrationRow(resident);
                        }),
                      )
                      .join("\r\n"),
                  ),
                  { from: "UNICODE", to: "SJIS" },
                ),
              ),
            ],
            { type: "text/csv" },
          ),
          t("%SimpleIndependenceLevelBatchRegistration.7%", {
            $: { facilityName, now: Temporal.Now.plainDateTimeISO("Asia/Tokyo") },
          }),
        );

        saveAsFile(
          new Blob(
            [
              new Uint8Array(
                Encoding.convert(
                  Encoding.stringToCode(
                    BedBatchRegistrationHeaderRows({ t })
                      .concat(
                        Array.from(numberOfBeds.entries()).flatMap(function ([name, numberOfBeds]) {
                          return new Array(numberOfBeds).fill(
                            toBedBatchRegistrationRow({ roomName: name }),
                          );
                        }),
                      )
                      .join("\r\n"),
                  ),
                  { from: "UNICODE", to: "SJIS" },
                ),
              ),
            ],
            { type: "text/csv" },
          ),
          toPathSafeString(
            t("%BedFileName.0%", {
              $: {
                name: facilityName,
                now: Temporal.Now.plainDateTimeISO("Asia/Tokyo"),
              },
            }) + ".csv",
          ),
        );

        saveAsFile(
          new Blob(
            [
              new Uint8Array(
                Encoding.convert(
                  Encoding.stringToCode(
                    BedBatchAllocationHeaderRows({ t })
                      .concat(
                        allocations.map(function (allocation) {
                          return toBedBatchAllocationRow(allocation);
                        }),
                      )
                      .join("\r\n"),
                  ),
                  { from: "UNICODE", to: "SJIS" },
                ),
              ),
            ],
            { type: "text/csv" },
          ),
          t("%BedBatchAllocation.11%", {
            $: { facilityName, now: Temporal.Now.plainDateTimeISO("Asia/Tokyo") },
          }),
        );
      }

      setFiles(function (files) {
        return files.length > 0 ? [] : files;
      });
    },
    [files, t],
  );

  const fileUploadStyle = FileUploadStyle();

  return (
    <FileUpload.Root
      {...properties}
      acceptedFiles={files}
      className={cx(fileUploadStyle.root, className)}
      disabled={state.loading}
      maxFiles={1}
      onFileChange={function ({ acceptedFiles }) {
        setFiles(acceptedFiles);
      }}
    >
      <FileUpload.Dropzone className={fileUploadStyle.dropzone}>
        <span className={css({ pointerEvents: "none" })}>{t("%DragAndDrop.0%")}</span>
        <span className={css({ pointerEvents: "none" })}>{t("%Or.0%")}</span>
        <span className={css({ pointerEvents: "none" })}>{t("%Select.1%")}</span>
      </FileUpload.Dropzone>
      <FileUpload.HiddenInput />
    </FileUpload.Root>
  );
}
